import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  Theme,
  Typography,
} from '@mui/material';
import React, { useContext, useMemo, useState } from 'react';

import { DialogTitleWithClose } from '@/components/common/DialogTitleWithClose';
import { TokenContext } from '@/Contexts/TokenContext';
import { UsersContext } from '@/Contexts/UsersContext';
import useAuth from '@/hooks/useAuth';
import useUser from '@/hooks/useUser';
import { StartUserImpersonationProps } from '@/models/StartUserImpersonationProps';
import { StopUserImpersonationProps } from '@/models/StopUserImpersonationProps';
import { UserProps, UserType, userTypeAsString } from '@/models/User';

import { Spinner } from '../../common/Spinner';

type UserImpersonateModalProps = {
  userImpersonateModalOpen: boolean;
  targetUser: UserProps | null;
  theme: Theme;
  setUserImpersonateModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  handleRowUpdate: (usersToUpdate: UserProps[]) => void;
};

export default function UserImpersonateModal({
  userImpersonateModalOpen,
  targetUser,
  theme,
  setUserImpersonateModalOpen,
  handleRowUpdate,
}: UserImpersonateModalProps) {
  const { startUserImpersonation, stopUserImpersonation } = useUser();
  const { logoutCurrentUser } = useAuth();
  const { b2cId } = useContext(TokenContext);

  const { usersAll, userCurrent } = useContext(UsersContext);

  const [postingUser, setPostingUser] = useState(false);

  const loading = !usersAll || !b2cId;

  // If the selected row is an ADF user impersonating someone,
  // show the impersonated user instead (so the modal focuses on the impersonatee).
  const resolvedTarget = useMemo<UserProps | undefined>(() => {
    if (!targetUser || !usersAll) return undefined;

    if (targetUser.userAccountType === UserType.Adf && targetUser.userImpersonated) {
      return usersAll.find((u) => u.b2CId === targetUser.userImpersonated) ?? targetUser;
    }

    return targetUser;
  }, [targetUser, usersAll]);

  const isBeingImpersonated = Boolean(resolvedTarget?.userImpersonatedBy);

  // When stopping, the impersonator is the user referenced by userImpersonatedBy on the impersonatee.
  const activeImpersonator = useMemo(() => {
    if (!resolvedTarget?.userImpersonatedBy || !usersAll) return undefined;
    return usersAll.find((u) => u.b2CId === resolvedTarget.userImpersonatedBy);
  }, [resolvedTarget?.userImpersonatedBy, usersAll]);

  const handleClose = () => setUserImpersonateModalOpen(false);

  const updateUserImpersonate = async () => {
    if (!resolvedTarget) return;

    setPostingUser(true);

    try {
      if (isBeingImpersonated) {
        if (!activeImpersonator || !resolvedTarget.b2CId || !resolvedTarget.userImpersonatedBy)
          return;

        await stopUserImpersonation({
          impersonateeB2CId: resolvedTarget.b2CId,
          impersonatorB2CId: resolvedTarget.userImpersonatedBy,
        } satisfies StopUserImpersonationProps);

        setUserImpersonateModalOpen(false);
        return;
      }

      // START: use current logged-in user as impersonator
      if (!resolvedTarget?.b2CId || !userCurrent?.b2CId) return;

      const { impersonatee, impersonator } = await startUserImpersonation({
        impersonatorB2CId: userCurrent.b2CId,
        impersonateeB2CId: resolvedTarget.b2CId,
      } satisfies StartUserImpersonationProps);

      handleRowUpdate([impersonator, impersonatee]);
      void logoutCurrentUser('Impersonation');

      setUserImpersonateModalOpen(false);
    } finally {
      setPostingUser(false);
    }
  };

  const title = isBeingImpersonated ? 'Stop Impersonation' : 'Start Impersonation';
  const actionLabel = isBeingImpersonated ? 'Stop Impersonating' : 'Start Impersonating';

  return (
    <Dialog open={userImpersonateModalOpen} onClose={handleClose}>
      <DialogTitleWithClose onClose={handleClose}>{title}</DialogTitleWithClose>

      <DialogContent>
        {loading ? (
          <Spinner type='circular' />
        ) : (
          <>
            <Box sx={{ mb: 2 }}>
              <Typography variant='overline2'>Applicant</Typography>
              <Typography color='textSecondary'>
                {`${resolvedTarget?.contact?.firstName ?? ''} ${
                  resolvedTarget?.contact?.lastName ?? ''
                } (${resolvedTarget?.email ?? ''})`}
              </Typography>
            </Box>

            {resolvedTarget?.userType != null && (
              <Box sx={{ mb: 2 }}>
                <Typography variant='overline2'>User Type</Typography>
                <Typography color='textSecondary'>
                  {userTypeAsString(resolvedTarget.userType)}
                </Typography>
              </Box>
            )}

            {resolvedTarget?.userType === UserType.Applicant && (
              <Box sx={{ mb: 2 }}>
                <Typography variant='overline2'>Partner Group</Typography>
                <Typography color='textSecondary'>
                  {resolvedTarget.partnerGroup?.partnerName}
                </Typography>
              </Box>
            )}
          </>
        )}
      </DialogContent>

      <DialogActions>
        {!loading && (
          <>
            <Button variant='secondary' size='large' onClick={handleClose}>
              Cancel
            </Button>

            <Button
              variant='primary'
              size='large'
              onClick={updateUserImpersonate}
              disabled={
                postingUser ||
                (isBeingImpersonated && !activeImpersonator) ||
                (!isBeingImpersonated && !userCurrent)
              }
            >
              {postingUser ? (
                <>
                  <span>Updating ... </span>
                  <CircularProgress
                    style={{ color: theme.palette.grey[500], marginLeft: 5 }}
                    size='16px'
                  />
                </>
              ) : (
                actionLabel
              )}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}
